import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { TextAreaComponent } from '../../components/text-area/text-area.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { AiAdminService } from '../../services/ai-admin.service';
import { HttpService } from '../../services/http.service';

@Component({
  selector: 'app-ai-admin-list-page',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridListComponent, HeaderComponent, SelectInputComponent, ListActionButtonsComponent, TextAreaComponent],
  templateUrl: './ai-admin-list-page.component.html',
})
export class AiAdminListPageComponent implements OnInit {

  page_name = 'Login Request';
  params: any = {};
  items: any = { data: [], count: 0 };

  university_options: any[] = [];
  role_options: any[] = [];

  show_review_modal = false;
  selected_request: any = null;
  show_approve_modal = false;
  show_reject_modal = false;
  action_loading = false;
  review_comment = '';

  columns: any = [
    {
      headerName: 'Name', flex: 2, minWidth: 170,
      cellRenderer: (p: any) => {
        const name = p.data?.User?.name || '-';
        const email = p.data?.User?.email || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:14px;font-weight:500;color:#212121;line-height:1.3">${name}</span>
          ${email ? `<span style="font-size:12px;color:#6b7280;line-height:1.3">${email}</span>` : ''}
        </div>`;
      }
    },
    {
      headerName: 'Contact', flex: 1.5, minWidth: 150,
      cellRenderer: (p: any) => {
        const contact = p.data?.User?.contact ? `+91${p.data.User.contact}` : '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#212121">${contact}</div>`;
      }
    },
    {
      headerName: 'Captured Date & Time', flex: 1.8, minWidth: 170,
      valueGetter: (p: any) => {
        if (!p.data?.created_at) return '-';
        const d = new Date(p.data.created_at);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const h12 = d.getHours() % 12 || 12;
        const min = String(d.getMinutes()).padStart(2, '0');
        const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
        return `${dd}/${mm}/${yyyy} ${String(h12).padStart(2, '0')}:${min}${ampm}`;
      }
    },
    {
      headerName: 'Request ID', flex: 1.5, minWidth: 140,
      cellRenderer: (p: any) => {
        const rid = p.data?.request_id || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:500;color:#212121">${rid}</div>`;
      }
    },
    {
      headerName: 'Confidence Score', flex: 1.3, minWidth: 140,
      cellRenderer: (p: any) => {
        const score = p.data?.confidence_score;
        if (score == null) return `<div style="display:flex;align-items:center;height:100%;font-size:14px;color:#9ca3af">—</div>`;
        const pct = Math.round(score * 100);
        const color = pct >= 80 ? '#15803D' : pct >= 40 ? '#B45309' : '#DC2626';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:700;color:${color}">${pct}%</div>`;
      }
    },
  ];

  actions(row: any) {
    const actions: any[] = [];
    actions.push({ type: 'button', text: 'Review', function: this.open_review.bind(this) });
    return actions;
  }

  constructor(
    public gs: GlobalService,
    public dds: DropdownService,
    public ai: AiAdminService,
    public hs: HttpService,
  ) { }

  async ngOnInit() {
    // Access Level: lock the filters this user cannot change before the first fetch.
    this.gs.apply_access_scope_to_params(this.params);
    this.columns.push({ headerName: 'Actions', sortable: false, filter: false, cellRenderer: ListActionButtonsComponent, headerClass: 'custom-header-center', cellStyle: { 'justify-content': 'center' }, cellRendererParams: { actions: this.actions.bind(this) }, editable: false, pinned: 'right', width: 130, minWidth: 130, maxWidth: 130 });
    await this.dds.get_dropdown_list(['university', 'role']);
    this.university_options = this.dds.dropdown_list?.university_list || [];
    this.role_options = this.dds.dropdown_list?.role_list || [];
    await this.get_list();
  }

  async get_list(extra?: any) {
    try {
      const res: any = await this.ai.get_login_request_list({ ...this.params, ...extra });
      this.items = res;
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to fetch login requests', 'Error', 'error');
    }
  }

  apply_filters() {
    this.get_list();
  }

  on_university_change() {
    this.params.role_id = null;
    this.apply_filters();
  }

  open_review(request: any) {
    this.selected_request = request;
    this.review_comment = '';
    this.show_review_modal = true;
  }

  close_review() {
    this.show_review_modal = false;
    this.selected_request = null;
    this.review_comment = '';
  }

  on_reject_click() {
    if (!this.review_comment.trim()) {
      this.gs.toastr_shows_function('Comment is required when rejecting', 'Error', 'error');
      return;
    }
    this.show_reject_modal = true;
  }

  format_submitted_date(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const h12 = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'pm' : 'am';
    return `${dd}/${mm}/${yyyy}  ${h12}:${min}${ampm}`;
  }

  get captured_image_url(): string {
    const path = this.selected_request?.captured_image;
    if (!path) return '';
    return path.startsWith('http') ? path : `${this.hs.base_url}${path}`;
  }

  get confidence_score_display(): string {
    const score = this.selected_request?.confidence_score;
    if (score == null) return '—';
    const pct = Math.round(score * 100);
    return `${pct > 0 ? '' : '-'}${Math.abs(pct)}%`;
  }

  get confidence_color(): string {
    const score = this.selected_request?.confidence_score;
    if (score == null) return '#9CA3AF';
    const pct = Math.round(score * 100);
    return pct >= 80 ? '#15803D' : pct >= 40 ? '#B45309' : '#DC2626';
  }

  async approve() {
    if (!this.selected_request || this.action_loading) return;
    this.action_loading = true;
    try {
      await this.ai.approve_request({ id: this.selected_request.id });
      this.gs.toastr_shows_function('Request approved successfully', 'Success', 'success');
      this.show_approve_modal = false;
      this.close_review();
      await this.get_list();
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to approve', 'Error', 'error');
    } finally {
      this.action_loading = false;
    }
  }

  async reject() {
    if (!this.selected_request || this.action_loading) return;
    this.action_loading = true;
    try {
      await this.ai.reject_request({ id: this.selected_request.id, comment: this.review_comment.trim() });
      this.gs.toastr_shows_function('Request rejected successfully', 'Success', 'success');
      this.show_reject_modal = false;
      this.close_review();
      await this.get_list();
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to reject', 'Error', 'error');
    } finally {
      this.action_loading = false;
    }
  }
}
